import React, { useState, useEffect, useRef } from 'react';
import { Box, Tabs, Tab, Typography, CircularProgress, Alert, Button, Chip, Paper, Container, IconButton } from '@mui/material';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import { API_BASE } from '../config';

function SwaggerViewer() {
  const [categories, setCategories] = useState([]);
  const [selectedTab, setSelectedTab] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [specs, setSpecs] = useState({});
  const [deepLinkLoading, setDeepLinkLoading] = useState(false);
  const timerRef = useRef(null);
  const isDeepLinkingRef = useRef(false); // Track if deep linking is in progress

  // Parse URL hash to extract category, section, and operation
  const parseUrlHash = () => {
    const hash = window.location.hash.slice(1); // Remove #
    console.log('parseUrlHash - Raw hash:', window.location.hash);
    console.log('parseUrlHash - Hash after slice:', hash);
    if (!hash) return null;
    
    // Expected format: #/category/section/operation or #/category
    const parts = hash.split('/').filter(p => p);
    console.log('parseUrlHash - Parts after split:', parts);
    console.log('parseUrlHash - Parts length:', parts.length);
    if (parts.length === 0) return null;
    
    const result = {
      category: parts[0],
      section: parts[1] || null,
      operation: parts[2] || null
    };
    console.log('parseUrlHash - Result:', result);
    return result;
  };

  // Update URL hash based on current state
  const updateUrlHash = (category, section = null, operation = null) => {
    let newHash = `#/${category}`;
    if (section) newHash += `/${section}`;
    if (operation) newHash += `/${operation}`;
    window.history.replaceState(null, '', newHash);
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE}/swagger-categories`);
        if (response.ok) {
          const cats = await response.json();
          console.log('Available categories:', cats.map(c => c.id));
          setCategories(cats);
          
          // Check for pending deep link first (hash might have been cleared)
          const pendingHash = sessionStorage.getItem('pendingDeepLink');
          let urlData = parseUrlHash();
          
          // If hash was cleared but we have a pending deep link, parse that instead
          if (!urlData && pendingHash) {
            const tempHash = pendingHash.slice(1); // Remove #
            const parts = tempHash.split('/').filter(p => p);
            if (parts.length > 0) {
              urlData = {
                category: parts[0],
                section: parts[1] || null,
                operation: parts[2] || null
              };
              console.log('Using pending deep link for category selection:', urlData);
            }
          }
          
          console.log('URL data from hash:', urlData);
          
          if (urlData) {
            const categoryMatch = cats.some(c => c.id === urlData.category);
            // Pass user-controlled value as a %s argument, never as part of the format string itself
            console.log('Category "%s" exists in categories:', urlData.category, categoryMatch);
            
            if (categoryMatch) {
              console.log('Setting tab from URL hash:', urlData.category);
              
              // If we have an operation deep link, temporarily clear the hash
              // to prevent SwaggerUI from processing it incorrectly
              if (urlData.operation) {
                const originalHash = window.location.hash;
                
                // Only store if not already stored (prevents React strict mode from overwriting with empty hash)
                if (!sessionStorage.getItem('pendingDeepLink') && originalHash) {
                  console.log('!!! Temporarily hiding hash from SwaggerUI to prevent truncation');
                  sessionStorage.setItem('pendingDeepLink', originalHash);
                  // Clear the hash temporarily
                  window.history.replaceState(null, '', window.location.pathname + window.location.search);
                } else {
                  console.log('!!! Pending deep link already stored, skipping hash clear');
                }
              }
              
              setSelectedTab(urlData.category);
              // DON'T call updateUrlHash - preserve the full hash including section/operation
            } else {
              console.warn(`Category "${urlData.category}" not found in available categories`);
              
              // Try to find if urlData.category is actually a section/tag in another category
              // This happens when users share URLs like #/OEM/Unload%20api/endpoint
              // where "OEM" is a tag within the "API" category, not a category itself
              console.log('Searching for section/tag:', urlData.category, 'in all categories...');
              
              // Store the original hash to prevent SwaggerUI from processing it incorrectly
              if (urlData.operation) {
                const originalHash = window.location.hash;
                
                // Only store if not already stored (prevents React strict mode from overwriting with empty hash)
                if (!sessionStorage.getItem('pendingDeepLink') && originalHash) {
                  console.log('!!! Temporarily hiding hash from SwaggerUI during category search');
                  sessionStorage.setItem('pendingDeepLink', originalHash);
                  // Clear the hash temporarily
                  window.history.replaceState(null, '', window.location.pathname + window.location.search);
                }
              }
              
              // We'll defer this check until specs are loaded
              // Store the intended section for later
              sessionStorage.setItem('pendingSectionSearch', urlData.category);
              if (urlData.section) sessionStorage.setItem('pendingSection', urlData.section);
              if (urlData.operation) sessionStorage.setItem('pendingOperation', urlData.operation);
              
              // Default to first category for now
              if (cats.length > 0) {
                console.log('Temporarily setting tab to first category:', cats[0].id, '(will search for correct category after specs load)');
                setSelectedTab(cats[0].id);
              }
            }
          } else {
            // Check for shared category from sessionStorage
            const sharedCategory = sessionStorage.getItem('openSwaggerCategory');
            if (sharedCategory && cats.some(c => c.id === sharedCategory)) {
              console.log('Setting tab from shared category:', sharedCategory);
              setSelectedTab(sharedCategory);
              // Only update hash if there isn't already a full hash
              if (!urlData || !urlData.section) {
                updateUrlHash(sharedCategory);
              }
              sessionStorage.removeItem('openSwaggerCategory');
            } else if (cats.length > 0 && !selectedTab) {
              console.log('Setting tab to first category:', cats[0].id);
              setSelectedTab(cats[0].id);
              // Only update hash if there isn't already a full hash
              if (!urlData || !urlData.section) {
                updateUrlHash(cats[0].id);
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        setError('Failed to load categories');
      }
    };

    fetchCategories();
  }, []); // Removed selectedTab from dependency array to prevent re-fetching

  // Handle shared category that might be set after component mount (race condition fix)
  useEffect(() => {
    if (categories.length === 0) return;
    
    const checkSharedCategory = () => {
      const sharedCategory = sessionStorage.getItem('openSwaggerCategory');
      if (sharedCategory && categories.some(c => c.id === sharedCategory)) {
        console.log('Late-arriving shared category detected:', sharedCategory);
        setSelectedTab(sharedCategory);
        sessionStorage.removeItem('openSwaggerCategory');
      }
    };

    // Check immediately
    checkSharedCategory();

    // Also check after a short delay to handle async timing issues
    const timer = setTimeout(checkSharedCategory, 100);
    
    return () => clearTimeout(timer);
  }, [categories]);

  useEffect(() => {
    if (categories.length === 0) return;

    const loadAllSpecs = async () => {
      setLoading(true);
      setError(null);
      const newSpecs = {};

      try {
          const response = await fetch(`${API_BASE}/swagger-documents`);
        if (response.ok) {
          const allDocs = await response.json();
          
          categories.forEach(category => {
            const categoryDocs = allDocs.filter(doc => doc.category === category.id);
            if (categoryDocs.length > 0) {
              newSpecs[category.id] = categoryDocs[0].content;
            }
          });
        }
        setSpecs(newSpecs);
        
        // Check if we need to find the correct category for a section/tag
        const pendingSectionSearch = sessionStorage.getItem('pendingSectionSearch');
        if (pendingSectionSearch && Object.keys(newSpecs).length > 0) {
          console.log('Searching for section/tag:', pendingSectionSearch, 'in loaded specs...');
          
          let foundCategory = null;
          for (const [categoryId, spec] of Object.entries(newSpecs)) {
            // Check if this spec has a tag matching the search term
            if (spec && spec.tags) {
              const matchingTag = spec.tags.find(tag => 
                tag.name && tag.name.toLowerCase() === pendingSectionSearch.toLowerCase()
              );
              if (matchingTag) {
                foundCategory = categoryId;
                console.log('Found matching tag in category:', categoryId, 'tag:', matchingTag.name);
                break;
              }
            }
          }
          
          if (foundCategory) {
            console.log('Switching to correct category:', foundCategory);
            setSelectedTab(foundCategory);
            
            // Reconstruct the hash with the proper category
            const pendingSection = sessionStorage.getItem('pendingSection');
            const pendingOperation = sessionStorage.getItem('pendingOperation');
            
            // Build the corrected hash
            let correctedHash = `#/${foundCategory}`;
            if (pendingSection) correctedHash += `/${pendingSection}`;
            if (pendingOperation) correctedHash += `/${pendingOperation}`;
            
            // Update the pending deep link with the corrected hash
            sessionStorage.setItem('pendingDeepLink', correctedHash);
            console.log('Updated pendingDeepLink with corrected hash:', correctedHash);
          } else {
            console.log('Tag not found in any category, keeping original URL');
            // If we can't find the tag, we still need to let the deep link restoration happen
            // The URL might be valid in the context of the selected category
          }
          
          // Clean up session storage
          sessionStorage.removeItem('pendingSectionSearch');
          sessionStorage.removeItem('pendingSection');
          sessionStorage.removeItem('pendingOperation');
        }
      } catch (err) {
        setError('Failed to load API documentation');
        console.error('Error loading specs:', err);
      } finally {
        setLoading(false);
        
        // Note: pendingDeepLink restoration is now handled by handleHashScroll useEffect
        // which runs after specs are loaded and ensures proper category switching
      }
    };

    loadAllSpecs();
  }, [categories]);

  // Handle deep linking - scroll to hash target after SwaggerUI loads
  useEffect(() => {
    console.log('!!! ===== DEEP LINKING useEffect RUNNING =====');
    console.log('!!! Dependencies - loading:', loading, 'selectedTab:', selectedTab, 'categories:', categories.length, 'specs keys:', Object.keys(specs));
    console.log('!!! Current timer ref:', timerRef.current);
    
    // Helper functions defined at useEffect level for reusability
    const safeQueryById = (id) => {
      try {
        return document.querySelector(`#${CSS.escape(id)}`);
      } catch (e) {
        return document.querySelector(`[id="${id}"]`);
      }
    };
    
    const findByPartialId = (partial) => {
      const decoded = decodeURIComponent(partial);
      const elements = document.querySelectorAll('[id]');
      for (let el of elements) {
        if (el.id.includes(partial) || el.id.includes(decoded)) {
          return el;
        }
      }
      return null;
    };
    
    const findByOperationId = (opId) => {
      const decoded = decodeURIComponent(opId);
      const elements = document.querySelectorAll('.opblock');
      
      console.log('Searching for operation ID:', opId, 'Decoded:', decoded);
      console.log('Total opblocks found:', elements.length);
      
      let foundMatch = null;
      let allOperationIds = [];
      
      for (let el of elements) {
        // Collect all IDs for debugging
        if (el.id) {
          allOperationIds.push(el.id);
        }
        
        // Check ID attribute directly - must be exact match or end with the operation ID
        if (el.id) {
          // SwaggerUI formats IDs as "operations-{tag}-{operationId}"
          if (el.id.endsWith(`-${opId}`) || el.id.endsWith(`-${decoded}`) || 
              el.id === `operations-${opId}` || el.id === `operations-${decoded}`) {
            console.log('Found by ID attribute:', el.id);
            foundMatch = el;
            break;
          }
        }
        
        // Check data-operation-id attribute
        if (el.dataset.operationId === decoded || el.dataset.operationId === opId) {
          console.log('Found by data-operationId:', el.dataset.operationId);
          foundMatch = el;
          break;
        }
      }
      
      if (!foundMatch) {
        console.log('Operation not found. Available operation IDs:', allOperationIds.filter(id => id.includes('billing') || id.includes('Billing')));
      }
      
      return foundMatch;
    };
    
    const findByMethodAndPath = (opId) => {
      const decoded = decodeURIComponent(opId);
      const elements = document.querySelectorAll('.opblock');
      
      console.log('Searching by method and path for:', decoded);
      
      for (let el of elements) {
        const pathEl = el.querySelector('.opblock-summary-path');
        const methodEl = el.querySelector('.opblock-summary-method');
        if (pathEl && methodEl) {
          const path = pathEl.textContent.trim();
          const method = methodEl.textContent.trim().toLowerCase();
          
          // Try different combinations
          const patterns = [
            `${method}_${path}`.replace(/[^a-zA-Z0-9_]/g, '_'),
            `${method}${path}`.replace(/[^a-zA-Z0-9_]/g, '_'),
            path.replace(/[^a-zA-Z0-9_]/g, '_'),
            path.replace(/\//g, '_')
          ];
          
          console.log('Checking patterns:', patterns, 'against', decoded);
          
          for (let pattern of patterns) {
            if (decoded.includes(pattern) || pattern.includes(decoded)) {
              console.log('Found by pattern match:', pattern);
              return el;
            }
          }
          
          // Also check if the decoded operation ID contains parts of the path
          // Exclude common parts that appear in most APIs (api, v1, v2, etc.)
          const commonParts = ['api', 'v1', 'v2', 'v3', 'v4'];
          const pathParts = path.split('/').filter(p => p);
          const decodedParts = decoded.toLowerCase().split('_').filter(p => p && !commonParts.includes(p));
          const meaningfulPathParts = pathParts.filter(p => {
            const cleanPart = p.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
            return !commonParts.includes(cleanPart) && cleanPart.length > 1;
          });
          
          let matches = 0;
          for (let part of meaningfulPathParts) {
            const cleanPart = part.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
            // Only count as match if the part is meaningful (not a variable placeholder)
            if (!cleanPart.startsWith('{') && decodedParts.some(dp => dp.includes(cleanPart) || cleanPart.includes(dp))) {
              matches++;
            }
          }
          
          // Require at least 2 meaningful matches, or if path is very specific (1 part), require 1 match
          if (matches >= 2 || (meaningfulPathParts.length === 1 && matches === 1)) {
            console.log('Found by path parts match:', path, 'matches:', matches, 'meaningful parts:', meaningfulPathParts.length);
            return el;
          }
        }
      }
      return null;
    };
    
    const safeQueryByAttribute = (attribute, value, partial = false) => {
      try {
        if (partial) {
          return document.querySelector(`[${attribute}*="${CSS.escape(value)}"]`);
        } else {
          return document.querySelector(`[${attribute}="${CSS.escape(value)}"]`);
        }
      } catch (e) {
        const elements = document.querySelectorAll(`[${attribute}]`);
        for (let el of elements) {
          const attrValue = el.getAttribute(attribute);
          if (partial ? attrValue.includes(value) : attrValue === value) {
            return el;
          }
        }
        return null;
      }
    };
    
    const handleHashScroll = () => {
      let urlData = parseUrlHash();
      let wasFromPendingDeepLink = false;
      let pendingHashToRestore = null;
      
      // Check for pending deep link even if there's no current hash
      // This handles post-login restoration
      if (!urlData || !urlData.category) {
        const pendingHash = sessionStorage.getItem('pendingDeepLink');
        if (pendingHash && !loading && specs[selectedTab]) {
          console.log('!!! Found pendingDeepLink with no current hash, processing:', pendingHash);
          
          // Parse the pending hash
          const hashParts = pendingHash.slice(2).split('/').filter(p => p);
          if (hashParts.length > 0) {
            const hashCategory = hashParts[0];
            
            // Check if we need to switch categories
            if (categories.some(c => c.id === hashCategory)) {
              if (hashCategory !== selectedTab) {
                console.log('!!! Need to switch to category:', hashCategory, 'first');
                setSelectedTab(hashCategory);
                // Don't remove pendingDeepLink yet, let it be picked up after tab switch
                return;
              }
              
              // We're on the right tab, parse as urlData but DON'T set hash yet
              console.log('!!! On correct tab, parsing pendingDeepLink as urlData (NOT setting hash yet)');
              urlData = {
                category: hashParts[0],
                section: hashParts[1] || null,
                operation: hashParts[2] || null
              };
              
              wasFromPendingDeepLink = true;
              pendingHashToRestore = pendingHash;
              
              // DON'T restore hash yet - wait until after we find and scroll to the operation
              // This prevents SwaggerUI from truncating it
              sessionStorage.removeItem('pendingDeepLink');
              
              // Continue processing with this urlData
            } else {
              console.warn('!!! Category from pendingDeepLink not found:', hashCategory);
              sessionStorage.removeItem('pendingDeepLink');
              return;
            }
          }
        }
        
        // If still no urlData, nothing to do
        if (!urlData) return;
      }
      
      if (!loading && specs[selectedTab] && urlData) {
        // CRITICAL: Only search if we're in the correct category!
        // If URL says #/BILLING/... but selectedTab is still "API", switch tabs first
        if (urlData.category && urlData.category !== selectedTab) {
          console.log(`!!! Category mismatch: URL has "${urlData.category}" but selectedTab is "${selectedTab}".`);
          
          // Check if the category exists
          if (categories.some(c => c.id === urlData.category)) {
            console.log('!!! Switching to correct category:', urlData.category);
            setSelectedTab(urlData.category);
            
            // Restore hash immediately after tab switch if it was from pending deep link
            if (wasFromPendingDeepLink && pendingHashToRestore) {
              setTimeout(() => {
                console.log('!!! Restoring hash after tab switch:', pendingHashToRestore);
                window.history.replaceState(null, '', pendingHashToRestore);
              }, 500);
            }
            // The useEffect listening to selectedTab will re-trigger handleHashScroll
            return;
          } else {
            console.warn('!!! Category not found:', urlData.category);
            
            // Still restore hash
            if (wasFromPendingDeepLink && pendingHashToRestore) {
              console.log('!!! Restoring hash despite category not found:', pendingHashToRestore);
              window.history.replaceState(null, '', pendingHashToRestore);
            }
            return;
          }
        }
        
        console.log('Deep linking triggered. URL data:', urlData);
        console.log('Searching in category:', selectedTab);
        
        // Show loading indicator if we're searching for a specific operation
        if (urlData.operation) {
          console.log('!!! Setting deepLinkLoading to TRUE');
          setDeepLinkLoading(true);
          isDeepLinkingRef.current = true; // Mark that deep linking is in progress
        } else {
          console.log('!!! No operation specified, skipping loading indicator');
        }
        
        // Clear any existing timer
        if (timerRef.current) {
          console.log('!!! Clearing existing timer:', timerRef.current);
          clearTimeout(timerRef.current);
        }
        
        console.log('!!! Setting up new timer with 2000ms delay');
        // Give SwaggerUI time to render
        timerRef.current = setTimeout(() => {
          console.log('!!! ===== TIMER CALLBACK EXECUTING AFTER 2 SECONDS =====');
          let element = null;
          
          // Try to find the specific operation
          if (urlData.operation) {
            const operationId = decodeURIComponent(urlData.operation);
            const sectionId = decodeURIComponent(urlData.section || '');
            
            console.log('=== SEARCHING FOR OPERATION ===');
            console.log('Operation:', operationId);
            console.log('Section:', sectionId);
            console.log('Full URL Data:', urlData);
            
            // Function to search for the operation
            const searchForOperation = () => {
              const found = safeQueryById(`operations-${sectionId}-${operationId}`) ||
                           safeQueryById(`operations-${operationId}`) ||
                           findByOperationId(operationId) ||
                           findByMethodAndPath(operationId) ||
                           findByPartialId(operationId) ||
                           safeQueryByAttribute('data-path', operationId, true);
              
              console.log('=== OPERATION ELEMENT FOUND:', found, '===');
              return found;
            };
            
            // First, try to find and expand the section/tag
            if (sectionId) {
              const sectionElement = safeQueryById(`operations-tag-${sectionId}`) ||
                                    safeQueryByAttribute('data-tag', sectionId, false) ||
                                    findByPartialId(sectionId);
              
              if (sectionElement) {
                console.log('Found section element:', sectionElement);
                // Check if section is collapsed and expand it
                const expandButton = sectionElement.querySelector('.expand-operation') ||
                                    sectionElement.querySelector('button');
                if (expandButton && expandButton.getAttribute('aria-expanded') === 'false') {
                  console.log('Expanding section...');
                  expandButton.click();
                  // Wait for section to expand before searching for operation
                  setTimeout(() => {
                    console.log('Section expanded, now searching for operation...');
                    element = searchForOperation();
                    if (element) {
                      setDeepLinkLoading(false); // Hide loader when found
                      isDeepLinkingRef.current = false; // Deep linking complete
                      
                      // Restore hash if this was from a pending deep link
                      if (wasFromPendingDeepLink && pendingHashToRestore) {
                        console.log('!!! Restoring hash after finding operation (section callback):', pendingHashToRestore);
                        setTimeout(() => {
                          window.history.replaceState(null, '', pendingHashToRestore);
                        }, 100);
                      }
                      
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      
                      const opblock = element.closest('.opblock') || element.querySelector('.opblock') || element;
                      if (opblock) {
                        const isCollapsed = opblock.classList.contains('is-closed') || 
                                           opblock.querySelector('[aria-expanded="false"]');
                        if (isCollapsed) {
                          const button = opblock.querySelector('.opblock-summary-control') ||
                                       opblock.querySelector('.opblock-summary') ||
                                       opblock.querySelector('button[aria-label*="Expand"]') ||
                                       opblock.querySelector('.opblock-summary-method');
                          if (button) {
                            console.log('!!! Clicking expand button after section expansion !!!');
                            button.click();
                            setTimeout(() => element.scrollIntoView({ behavior: 'smooth', block: 'start' }), 600);
                          } else {
                            opblock.click();
                          }
                        }
                      }
                    }
                  }, 800);
                  return; // Exit early, we'll handle it in the callback
                }
              }
            }
            
            // If section wasn't collapsed or wasn't found, search immediately
            element = searchForOperation();
            
          } else if (urlData.section) {
            const sectionId = decodeURIComponent(urlData.section);
            
            console.log('Looking for section:', sectionId);
            
            // Try to find the tag/section
            element = safeQueryById(`operations-tag-${sectionId}`) ||
                     safeQueryByAttribute('data-tag', sectionId, false) ||
                     findByPartialId(sectionId);
            
            console.log('Section element found:', element);
          }
          
          console.log('Found element:', element);
          
          if (element) {
            setDeepLinkLoading(false); // Hide loader when found
            isDeepLinkingRef.current = false; // Deep linking complete
            
            // Restore hash if this was from a pending deep link
            if (wasFromPendingDeepLink && pendingHashToRestore) {
              console.log('!!! Restoring hash after finding operation:', pendingHashToRestore);
              setTimeout(() => {
                window.history.replaceState(null, '', pendingHashToRestore);
              }, 100);
            }
            
            setTimeout(() => {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              
              // Try to expand the operation if it's collapsed
              const opblock = element.closest('.opblock') || element.querySelector('.opblock') || element;
              console.log('Opblock element:', opblock);
              console.log('Opblock classes:', opblock ? opblock.className : 'none');
              console.log('Has is-closed class:', opblock ? opblock.classList.contains('is-closed') : false);
              
              if (opblock) {
                // Check if it's collapsed (has is-closed class or aria-expanded=false)
                const isCollapsed = opblock.classList.contains('is-closed') || 
                                   opblock.querySelector('[aria-expanded="false"]');
                
                console.log('Operation is collapsed:', isCollapsed);
                
                if (isCollapsed) {
                  // Try multiple selectors to find the expand button
                  const button = opblock.querySelector('.opblock-summary-control') ||
                               opblock.querySelector('.opblock-summary') ||
                               opblock.querySelector('button[aria-label*="Expand"]') ||
                               opblock.querySelector('.opblock-summary-method');
                  
                  console.log('Expand button found:', button);
                  
                  if (button) {
                    console.log('!!! Clicking button to expand operation !!!');
                    button.click();
                    // Scroll again after expansion to ensure visibility
                    setTimeout(() => {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      console.log('Re-scrolled after expansion');
                    }, 600);
                  } else {
                    console.warn('Could not find expand button');
                    // Try clicking the opblock itself as a fallback
                    console.log('Trying to click opblock directly');
                    opblock.click();
                  }
                } else {
                  console.log('Operation is already expanded, no action needed');
                }
              }
            }, 300);
          } else {
            console.warn('Could not find element for URL data:', urlData);
            // Retry after additional delay in case SwaggerUI is still rendering
            if (urlData.operation) {
              console.log('=== RETRY #1: Waiting 2 seconds for SwaggerUI to render... ===');
              setTimeout(() => {
                const operationId = decodeURIComponent(urlData.operation);
                console.log('=== RETRY #1: Searching for:', operationId, '===');
                
                const retryElement = findByOperationId(operationId) ||
                                    findByMethodAndPath(operationId) ||
                                    findByPartialId(operationId) ||
                                    safeQueryById(`operations-${operationId}`) ||
                                    safeQueryByAttribute('data-path', operationId, true);
                
                if (retryElement) {
                  console.log('=== RETRY #1: SUCCESS! Found element:', retryElement, '===');
                  setDeepLinkLoading(false); // Hide loader when found
                  isDeepLinkingRef.current = false; // Deep linking complete
                  
                  // Restore hash if this was from a pending deep link
                  if (wasFromPendingDeepLink && pendingHashToRestore) {
                    console.log('!!! Restoring hash after finding operation (retry 1):', pendingHashToRestore);
                    setTimeout(() => {
                      window.history.replaceState(null, '', pendingHashToRestore);
                    }, 100);
                  }
                  
                  retryElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  
                  const opblock = retryElement.closest('.opblock') || retryElement.querySelector('.opblock') || retryElement;
                  if (opblock) {
                    const isCollapsed = opblock.classList.contains('is-closed') || 
                                       opblock.querySelector('[aria-expanded="false"]');
                    if (isCollapsed) {
                      const button = opblock.querySelector('.opblock-summary-control') ||
                                   opblock.querySelector('.opblock-summary') ||
                                   opblock.querySelector('button[aria-label*="Expand"]') ||
                                   opblock.querySelector('.opblock-summary-method');
                      if (button) {
                        console.log('!!! RETRY #1: Clicking expand button !!!');
                        button.click();
                        setTimeout(() => {
                          retryElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 600);
                      } else {
                        opblock.click();
                      }
                    }
                  }
                } else {
                  console.log('=== RETRY #1: FAILED. Trying again in 3 seconds... ===');
                  // Second retry with even longer delay
                  setTimeout(() => {
                    console.log('=== RETRY #2: Searching for:', operationId, '===');
                    const secondRetry = findByOperationId(operationId) ||
                                       findByMethodAndPath(operationId) ||
                                       findByPartialId(operationId);
                    if (secondRetry) {
                      console.log('=== RETRY #2: SUCCESS! Found element:', secondRetry, '===');
                      setDeepLinkLoading(false); // Hide loader when found
                      isDeepLinkingRef.current = false; // Deep linking complete
                      
                      // Restore hash if this was from a pending deep link
                      if (wasFromPendingDeepLink && pendingHashToRestore) {
                        console.log('!!! Restoring hash after finding operation (retry 2):', pendingHashToRestore);
                        setTimeout(() => {
                          window.history.replaceState(null, '', pendingHashToRestore);
                        }, 100);
                      }
                      
                      secondRetry.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      
                      const opblock = secondRetry.closest('.opblock') || secondRetry.querySelector('.opblock') || secondRetry;
                      if (opblock) {
                        const isCollapsed = opblock.classList.contains('is-closed') || 
                                           opblock.querySelector('[aria-expanded="false"]');
                        if (isCollapsed) {
                          const button = opblock.querySelector('.opblock-summary-control') ||
                                       opblock.querySelector('.opblock-summary') ||
                                       opblock.querySelector('button[aria-label*="Expand"]') ||
                                       opblock.querySelector('.opblock-summary-method');
                          if (button) {
                            console.log('!!! RETRY #2: Clicking expand button !!!');
                            button.click();
                          } else {
                            opblock.click();
                          }
                        }
                      }
                    } else {
                      console.error('=== RETRY #2: FAILED. Operation not found:', operationId, '===');
                      setDeepLinkLoading(false); // Hide loader even if not found
                      isDeepLinkingRef.current = false; // Deep linking complete (failed)
                      
                      // Restore hash even if operation wasn't found
                      if (wasFromPendingDeepLink && pendingHashToRestore) {
                        console.log('!!! Restoring hash after failed search:', pendingHashToRestore);
                        window.history.replaceState(null, '', pendingHashToRestore);
                      }
                    }
                  }, 3000);
                }
              }, 2000);
            } else {
              setDeepLinkLoading(false); // Hide loader if no operation to search for
              isDeepLinkingRef.current = false; // Deep linking complete
              
              // Restore hash even if no operation
              if (wasFromPendingDeepLink && pendingHashToRestore) {
                console.log('!!! Restoring hash (no operation in URL):', pendingHashToRestore);
                window.history.replaceState(null, '', pendingHashToRestore);
              }
            }
          }
        }, 2000); // Reduced timeout for faster response
        console.log('!!! Timer created successfully. Timer ID:', timerRef.current);
      }
    };
    
    // Run immediately
    handleHashScroll();
    
    // Listen for hash changes
    window.addEventListener('hashchange', handleHashScroll);
    
    return () => {
      console.log('!!! ===== DEEP LINKING useEffect CLEANUP RUNNING =====');
      console.log('!!! Timer ref at cleanup:', timerRef.current);
      console.log('!!! Is deep linking in progress:', isDeepLinkingRef.current);
      window.removeEventListener('hashchange', handleHashScroll);
      if (timerRef.current) {
        // Only clear timer if NOT in the middle of deep linking
        if (!isDeepLinkingRef.current) {
          console.log('!!! CLEANUP: Clearing timer:', timerRef.current);
          clearTimeout(timerRef.current);
          timerRef.current = null;
        } else {
          console.log('!!! CLEANUP: Skipping timer clear - deep linking in progress');
        }
      }
    };
  }, [loading, specs, selectedTab, categories]); // Include all dependencies, but protect timer in cleanup

  // Monitor hash changes from SwaggerUI and ensure category is always included
  useEffect(() => {
    if (!selectedTab) return;

    let lastHash = window.location.hash;
    let isDeepLinking = false;
    
    // Check if initial URL has operation deep link
    const initialUrlData = parseUrlHash();
    if (initialUrlData && initialUrlData.operation) {
      console.log('!!! INITIAL DEEP LINK DETECTED - DISABLING HASH MONITORING !!!');
      isDeepLinking = true;
      // Keep monitoring disabled for 15 seconds
      setTimeout(() => {
        isDeepLinking = false;
        console.log('Deep linking period ended after 15s, resuming hash monitoring');
      }, 15000);
    }

    const handleSwaggerHashChange = () => {
      const hash = window.location.hash;
      
      // Only process if hash actually changed
      if (hash === lastHash) return;
      
      console.log('Hash changed from', lastHash, 'to', hash);
      lastHash = hash;

      if (!hash || hash === '#/' || hash === '#') return;

      const urlData = parseUrlHash();
      
      // Don't interfere if we're deep linking to a specific operation
      // Check if the URL has an operation (3 parts: category/section/operation)
      if (urlData && urlData.operation) {
        console.log('!!! Deep link with operation detected, DISABLING hash monitoring !!!', urlData);
        isDeepLinking = true;
        // Disable monitoring for 15 seconds while deep link completes
        setTimeout(() => {
          isDeepLinking = false;
          console.log('Deep linking period ended, resuming hash monitoring');
        }, 15000);
        return; // Don't modify the hash!
      }
      
      // Don't fix hash if we're still in deep linking period
      if (isDeepLinking) {
        console.log('!!! Still in deep linking period, SKIPPING hash fix !!!');
        return; // Don't modify the hash!
      }
      
      // If hash doesn't start with our category, prepend it
      // SwaggerUI generates hashes like #/tag/operation, we need #/category/tag/operation
      // But only fix if it's missing the category (not if it's a complete URL being navigated to)
      if (hash.startsWith('#/') && (!urlData || urlData.category !== selectedTab)) {
        const hashContent = hash.slice(2); // Remove '#/'
        if (hashContent && !hashContent.startsWith(selectedTab)) {
          const newHash = `#/${selectedTab}/${hashContent}`;
          console.log('Fixing hash from', hash, 'to', newHash);
          lastHash = newHash;
          window.history.replaceState(null, '', newHash);
        }
      }
    };

    // Only start polling if NOT deep linking initially
    let intervalId;
    if (!isDeepLinking) {
      console.log('Starting hash monitoring interval');
      intervalId = setInterval(handleSwaggerHashChange, 500);
      // Also listen to hash changes
      window.addEventListener('hashchange', handleSwaggerHashChange);
    } else {
      console.log('!!! SKIPPING interval start AND hashchange listener - deep linking active !!!');
      // Start interval after deep linking period
      setTimeout(() => {
        if (!isDeepLinking) {
          console.log('Starting delayed hash monitoring interval and listener');
          intervalId = setInterval(handleSwaggerHashChange, 500);
          window.addEventListener('hashchange', handleSwaggerHashChange);
        }
      }, 15000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      window.removeEventListener('hashchange', handleSwaggerHashChange);
    };
  }, [selectedTab]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    // Update URL hash when tab changes
    updateUrlHash(newValue);
  };



  return (
    <Box sx={{ width: '100%' }}>
      {/* Modern Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 2,
          mb: 3,
          borderRadius: 0,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: '500px',
            height: '500px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            zIndex: 0
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-30%',
            left: '-5%',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.08)',
            zIndex: 0
          }
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem',
              boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
            }}>
              🚀
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                MiM API Documentation
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Welcome to the <strong>MiM (Marmon In Middle) API Documentation</strong>. This documentation provides comprehensive details for developers and integrators to interact with the MiM platform.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Paper>

      {/* Share Instructions */}
      {selectedTab && specs[selectedTab] && (
        <Alert severity="info" sx={{ mb: 2, mx: 2 }}>
          <Typography variant="body2">
            💡 <strong>Tip:</strong> Click on any endpoint to expand it, then click the share icon to copy a direct link to that specific API endpoint.
          </Typography>
        </Alert>
      )}

      {/* Modern Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Tabs
          value={selectedTab || false}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            flex: 1,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              minHeight: 64,
            }
          }}
        >
          {categories.map((category) => (
            <Tab 
              key={category.id} 
              value={category.id}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: category.color === 'primary' ? '#1976d2' :
                               category.color === 'secondary' ? '#9c27b0' :
                               category.color === 'success' ? '#2e7d32' :
                               category.color === 'error' ? '#d32f2f' :
                               category.color === 'warning' ? '#ed6c02' :
                               category.color === 'info' ? '#0288d1' : '#666'
                    }}
                  />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {category.label}
                  </Typography>
                  <Chip 
                    label={category.id} 
                    size="small" 
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem',
                      bgcolor: category.color === 'blue' ? '#1976d2' :
                              category.color === 'green' ? '#2e7d32' :
                              category.color === 'orange' ? '#ed6c02' :
                              '#666',
                      color: '#fff'
                    }}
                  />
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      {/* Content Area */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && !specs[selectedTab] && categories.length > 0 && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            No Documentation Available
          </Typography>
          <Typography variant="body1" color="text.secondary">
            No Swagger documentation has been uploaded for the <strong>{categories.find(c => c.id === selectedTab)?.label}</strong> category yet.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Contact your administrator to upload documentation for this category.
          </Typography>
        </Paper>
      )}

      {!loading && !error && categories.length === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            No Categories Available
          </Typography>
          <Typography variant="body1" color="text.secondary">
            No API documentation categories have been created yet. Please contact your administrator to add categories and documentation.
          </Typography>
        </Paper>
      )}

      {!loading && !error && specs[selectedTab] && (
        <Box sx={{ 
          position: 'relative',
          '& .swagger-ui': {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
          }
        }}>
          {/* Deep Link Loading Overlay */}
          {deepLinkLoading && (
            <Box sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.3)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
              backdropFilter: 'blur(2px)'
            }}>
              <CircularProgress size={60} thickness={4} sx={{ color: 'white' }} />
              <Typography variant="h6" sx={{ color: 'white', mt: 2, fontWeight: 500 }}>
                Searching for operation...
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
                Please wait while we locate the API endpoint
              </Typography>
            </Box>
          )}
          
          <SwaggerUI 
            key={selectedTab}
            spec={specs[selectedTab]} 
            deepLinking={true}
            docExpansion="list"
            defaultModelsExpandDepth={1}
            persistAuthorization={true}
            filter={false}
          />
        </Box>
      )}


    </Box>
  );
}

export default SwaggerViewer;
